import { useState } from 'react'
import { AlertTriangle, Lock, Eye, TrendingUp, Shield, Zap, Clock, Bell } from 'lucide-react'

export default function DarkWebMonitorPage() {
  const [stats] = useState({
    threatsDetected: 847,
    exposureScore: '6.2/10',
    credentialsCompromised: 34,
    monitoredSources: 127,
  })

  const monitoringCategories = [
    {
      title: 'Identity & Access Credentials',
      icon: '🔐',
      color: 'from-red-600 to-red-500',
      items: [
        { name: 'Employee Credentials', status: 'Monitoring', count: 2847, threat: 'High' },
        { name: 'Session Tokens & Cookies', status: 'Monitoring', count: 156, threat: 'Critical' },
        { name: 'Privileged Accounts (Admin/Root)', status: 'Monitoring', count: 12, threat: 'Critical' },
        { name: 'C-Suite Credentials', status: 'Monitoring', count: 8, threat: 'Critical' },
        { name: 'Third-Party/Vendor Credentials', status: 'Monitoring', count: 34, threat: 'High' },
      ]
    },
    {
      title: 'Corporate Assets & IP',
      icon: '💼',
      color: 'from-orange-600 to-orange-500',
      items: [
        { name: 'Typosquatting Domains', status: 'Monitoring', count: 47, threat: 'Medium' },
        { name: 'Proprietary Source Code', status: 'Monitoring', count: 23, threat: 'Critical' },
        { name: 'Confidential Documents', status: 'Monitoring', count: 156, threat: 'High' },
        { name: 'SSL Certificates', status: 'Monitoring', count: 12, threat: 'High' },
        { name: 'API Keys & Cloud Credentials', status: 'Monitoring', count: 34, threat: 'Critical' },
      ]
    },
    {
      title: 'Financial Data',
      icon: '💳',
      color: 'from-yellow-600 to-yellow-500',
      items: [
        { name: 'Corporate Credit Cards', status: 'Monitoring', count: 67, threat: 'High' },
        { name: 'Cryptocurrency Wallets', status: 'Monitoring', count: 8, threat: 'Medium' },
        { name: 'Bank Account Details', status: 'Monitoring', count: 3, threat: 'Critical' },
        { name: 'Wire Transfer Instructions', status: 'Monitoring', count: 0, threat: 'None' },
        { name: 'Financial Records', status: 'Monitoring', count: 12, threat: 'High' },
      ]
    },
    {
      title: 'Brand & Reputation',
      icon: '🎯',
      color: 'from-purple-600 to-purple-500',
      items: [
        { name: 'Executive Doxing', status: 'Monitoring', count: 5, threat: 'High' },
        { name: 'Fake Social Profiles', status: 'Monitoring', count: 23, threat: 'Medium' },
        { name: 'Hacker Forum Mentions', status: 'Monitoring', count: 89, threat: 'High' },
        { name: 'Brand Impersonation', status: 'Monitoring', count: 12, threat: 'Medium' },
        { name: 'Leaked Internal Comms', status: 'Monitoring', count: 6, threat: 'High' },
      ]
    },
    {
      title: 'Technical Infrastructure',
      icon: '🖥️',
      color: 'from-blue-600 to-blue-500',
      items: [
        { name: 'Infected Endpoints (Botnet Logs)', status: 'Monitoring', count: 14, threat: 'High' },
        { name: 'Exposed RDP Access', status: 'Monitoring', count: 8, threat: 'Critical' },
        { name: 'VPN Credentials', status: 'Monitoring', count: 23, threat: 'Critical' },
        { name: 'Vulnerability Mentions', status: 'Monitoring', count: 34, threat: 'Medium' },
        { name: 'Network Device Access', status: 'Monitoring', count: 5, threat: 'High' },
      ]
    },
  ]

  const darkWebSources = [
    { name: 'Ransomware Leak Sites', items: 'Bulk files, contracts, databases', color: 'from-red-600 to-red-500' },
    { name: 'Telegram/IRC Channels', items: 'Real-time credential trading', color: 'from-blue-600 to-blue-500' },
    { name: 'Paste Sites (Pastebin)', items: 'Code, configs, database dumps', color: 'from-purple-600 to-purple-500' },
    { name: 'Dark Web Marketplaces', items: 'Access for sale, data trading', color: 'from-orange-600 to-orange-500' },
    { name: 'Hacker Forums', items: 'BreachForums, Exploit.in chatter', color: 'from-pink-600 to-pink-500' },
    { name: 'Infostealer Logs', items: 'Browser sessions, API tokens', color: 'from-red-600 to-red-500' },
  ]

  const recentFindings = [
    { type: 'Session Token Exposed', severity: 'Critical', source: 'Telegram Channel', action: 'Revoke & Alert', time: '2 min ago' },
    { type: 'Executive Home Address Doxed', severity: 'High', source: 'Hacker Forum', action: 'Notify Security', time: '15 min ago' },
    { type: 'Source Code Leaked', severity: 'Critical', source: 'GitHub Paste', action: 'DMCA & Incident', time: '1 hour ago' },
    { type: 'Employee Credentials in Combo List', severity: 'High', source: 'Dark Market', action: 'Reset Password', time: '3 hours ago' },
    { type: 'Corporate Domain Typosquatting', severity: 'Medium', source: 'Domain Monitor', action: 'C&D Letter', time: '6 hours ago' },
    { type: 'Fake Social Media Account', severity: 'Medium', source: 'Social Monitor', action: 'Report & Remove', time: '1 day ago' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-pink-600 to-rose-600 p-8 text-white shadow-2xl border border-red-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Eye className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Dark Web Monitor</h1>
                </div>
                <p className="text-red-100 text-lg font-semibold">Real-Time Threat Intelligence & Exposure Monitoring</p>
                <p className="text-red-200 text-sm mt-1">Credentials • Assets • Financial Data • Brand • Infrastructure</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.credentialsCompromised}</div>
                <div className="text-sm text-red-100">Exposures Found</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Threats Detected', value: stats.threatsDetected, icon: AlertTriangle, color: 'from-red-600 to-red-500' },
            { label: 'Exposure Score', value: stats.exposureScore, icon: TrendingUp, color: 'from-orange-600 to-orange-500' },
            { label: 'Credentials Compromised', value: stats.credentialsCompromised, icon: Lock, color: 'from-pink-600 to-pink-500' },
            { label: 'Sources Monitored', value: stats.monitoredSources, icon: Eye, color: 'from-purple-600 to-purple-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-6">
              <p className="text-red-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Monitoring Categories */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Dark Web Monitoring Categories</h2>
          <div className="space-y-6">
            {monitoringCategories.map((category, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6">
                <div className={`h-1 w-full bg-gradient-to-r ${category.color} rounded-full mb-4 shadow-lg`}></div>
                <div className="flex items-center space-x-3 mb-4">
                  <span className="text-3xl">{category.icon}</span>
                  <h3 className="text-2xl font-bold text-white">{category.title}</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  {category.items.map((item, iidx) => (
                    <div key={iidx} className="p-3 bg-slate-700/50 rounded-lg border border-blue-500/20">
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-white font-semibold text-sm">{item.name}</p>
                        <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                          item.threat === 'Critical' ? 'bg-red-500/20 text-red-300' :
                          item.threat === 'High' ? 'bg-orange-500/20 text-orange-300' :
                          item.threat === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-green-500/20 text-green-300'
                        }`}>
                          {item.threat}
                        </span>
                      </div>
                      <p className="text-blue-300 text-xs">Exposures: <span className="text-cyan-400 font-bold">{item.count}</span></p>
                      <p className="text-green-400 text-xs mt-1">✓ {item.status}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dark Web Sources */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Monitored Dark Web Sources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {darkWebSources.map((source, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${source.color} rounded-xl p-6 text-white border border-opacity-20 shadow-lg`}>
                <h4 className="font-bold text-lg mb-2">{source.name}</h4>
                <p className="text-sm opacity-90">{source.items}</p>
                <button className="mt-4 w-full bg-white/20 hover:bg-white/30 transition-all px-3 py-2 rounded-lg text-sm font-bold">
                  View Alerts
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Findings */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
            <Bell className="w-6 h-6 text-red-400" />
            <span>Recent Critical Findings</span>
          </h3>
          <div className="space-y-3">
            {recentFindings.map((finding, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg border border-blue-500/20 hover:border-blue-400/40 transition-all">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                      finding.severity === 'Critical' ? 'bg-red-500/20 text-red-300' :
                      'bg-orange-500/20 text-orange-300'
                    }`}>
                      {finding.severity}
                    </span>
                    <p className="text-white font-semibold">{finding.type}</p>
                  </div>
                  <div className="flex items-center space-x-4 mt-2 text-blue-300 text-xs">
                    <span>📡 {finding.source}</span>
                    <span>⏰ {finding.time}</span>
                  </div>
                </div>
                <button className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-white text-sm font-bold transition-all">
                  {finding.action}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Response Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Automated Responses */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <Zap className="w-6 h-6 text-yellow-400" />
              <span>Automated Response Actions</span>
            </h3>
            <div className="space-y-2">
              {[
                'Revoke compromised credentials',
                'Alert security team in real-time',
                'Rotate API keys immediately',
                'Reset all affected user passwords',
                'Block IPs associated with threats',
                'Generate incident ticket',
                'Notify C-suite of critical exposures',
                'Trigger multi-factor authentication reset',
              ].map((action, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-2 text-blue-300">
                  <span className="text-green-400">✓</span>
                  <span className="text-sm">{action}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Intelligence Integration */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <Shield className="w-6 h-6 text-blue-400" />
              <span>Intelligence Partners & Sources</span>
            </h3>
            <div className="space-y-2">
              {[
                'Flare Intelligence API Integration',
                'SpyCloud Breach Data',
                'Digital Shadows Underground Monitoring',
                'Sixgill Dark Web Intelligence',
                'FBI/CISA Threat Feeds',
                'Ransomware Leak Site Aggregators',
                'Custom Keyword Monitoring',
                'Telegram Channel Crawling',
              ].map((partner, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-2 text-blue-300">
                  <span className="text-purple-400">⚙️</span>
                  <span className="text-sm">{partner}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Exposure Score Details */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-2xl font-bold text-white mb-4">Exposure Risk Score Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { category: 'Credentials Exposure', score: '8.5/10', items: '34 exposed credentials' },
              { category: 'Asset Exposure', score: '7.2/10', items: '23 leaked source code files' },
              { category: 'Financial Exposure', score: '4.1/10', items: '3 card numbers detected' },
              { category: 'Brand Exposure', score: '6.8/10', items: '89 forum mentions' },
              { category: 'Infrastructure Exposure', score: '7.9/10', items: '14 infected endpoints' },
              { category: 'Overall Risk', score: '6.9/10', items: 'Medium-High Risk Level' },
            ].map((risk, idx) => (
              <div key={idx} className="p-4 bg-slate-700/50 rounded-lg border border-orange-500/20">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white font-semibold">{risk.category}</p>
                  <span className={`text-2xl font-black ${
                    parseFloat(risk.score) > 7 ? 'text-red-400' :
                    parseFloat(risk.score) > 5 ? 'text-orange-400' :
                    'text-yellow-400'
                  }`}>
                    {risk.score}
                  </span>
                </div>
                <p className="text-blue-300 text-sm">{risk.items}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
